import { findOnlyIndexOrThrow } from "@dhau/lang-extras";
import { useCallback, useState } from "preact/hooks";
import type { ComponentType } from "preact";
import { clsx } from "clsx";
import JoyrideCore from "react-joyride";
import type { Props as JoyrideProps } from "react-joyride";
import type { Palette } from "@brickme/project-core/src/model/picture.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import useLocalStorage from "@brickme/frontend-common/src/hooks/use-local-storage.ts";
import type { SourceImage } from "@brickme/frontend-common/src/source-image/source-image.ts";
import { usePicture } from "@brickme/frontend-common/src/picture-edit/picture-edit-context.tsx";
import Button from "~/components/button.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import ArrowLeftIcon from "~/components/icons/arrow-left.tsx";
import ArrowRightIcon from "~/components/icons/arrow-right.tsx";
import SummaryText from "~/components/summary-text.tsx";
import ChevronUpIcon from "~/components/icons/chevron-up-icon.tsx";
import { useSetColoursRequired } from "~/hooks/region/context.tsx";
import CheckIcon from "./check-icon.tsx";
import GridIcon from "./grid-icon.tsx";
import PictureRender from "./render/picture-render.tsx";
import LayoutMenu from "./layout/layout-menu.tsx";
import AdjustmentsMenu, {
	coloursButtonId,
	saturationButtonId,
} from "./adjustments/adjustments-menu.tsx";
import { EditorProvider } from "./editor-provider.tsx";
import CompleteKitOnlyMenu from "./complete/complete-kit-only-menu.tsx";
import type { Mode } from "./mode.ts";
import ConfirmRestartModal from "./confirm-restart-modal.tsx";
import LevelsIcon from "./levels-icon.tsx";
import ChooseLayoutOverlay from "./layout/choose-layout-overlay.tsx";
import TryItOutCompleteMenu from "./complete/try-it-out-complete-menu.tsx";
import CompleteColoursMenu from "./complete/complete-colours-menu.tsx";
import ColoursCostSummary from "./cost-summary/colours-cost-summary.tsx";
import EditorTooltip from "./editor-tooltip.tsx";
import classes from "./editor.module.css";

// Some typing incompatibility with preact, but works fine
const Joyride = JoyrideCore as ComponentType<JoyrideProps>;

const confirmRestartModalId = "confirm-restart-modal";

const adjustmentsButtonId = "editor-adjustments-button";

const coloursCostSummaryId = "colours-cost-summary";

type EditorProps = {
	readonly onRestart: () => void;
	readonly onBackToUpload: () => void;
	readonly numberOfKits: number;
	readonly code: string | undefined;
};

function Editor({
	numberOfKits,
	onRestart,
	onBackToUpload,
	code,
}: EditorProps) {
	const t = useTranslator();

	// Relies on layout being the first/default step
	const [showLayoutOverlay, setShowLayoutOverlay] = useState(true);
	const layoutOverlay = useCallback(
		(size: GenericSize<number>) => (
			<ChooseLayoutOverlay
				size={size}
				onClose={() => setShowLayoutOverlay(false)}
			/>
		),
		[],
	);

	// Mode
	const [mode, setMode] = useState<Mode>("kit-only");
	const setColoursRequired = useSetColoursRequired();
	const { picture, patchPicture } = usePicture();
	function onChangeMode(newMode: Mode) {
		setMode(newMode);
		if (newMode === "colour") {
			setColoursRequired();
			patchPicture({
				// Should be a better way to do this. Is always a hybrid palette mode so probably
				// ok for now in practice.
				paletteMode:
					picture.paletteMode.type === "hybrid"
						? {
								...picture.paletteMode,
								numberOfExtraColours: Math.max(
									0,
									20 - picture.paletteMode.palette.length,
								),
							}
						: picture.paletteMode,
				saturation: 0.05,
			});
		}
		setActiveStepIndex(
			findOnlyIndexOrThrow(steps, (s) => s.id === "adjustments"),
		);
	}

	// Step details
	const [showStepDetails, setShowStepDetails] = useLocalStorage<boolean>({
		key: "showStepDetails",
		defaultValue: true,
		parse: JSON.parse,
		stringify: JSON.stringify,
	});

	const { resetPicture } = usePicture();

	// Steps
	const [summaryOpen, setSummaryOpen] = useState<false | true | "cart">(false);
	const steps = [
		{
			id: "layout" as const,
			buttonId: "editor-layout-button",
			label: t("Layout"),
			icon: <GridIcon />,
			heading: t("Choose layout"),
			summary: t(
				"Select the layout variation depending on your number of kits owned.",
			),
			menu: <LayoutMenu numberOfKits={numberOfKits} />,
		},
		{
			id: "adjustments" as const,
			buttonId: adjustmentsButtonId,
			label: t("Adjust"),
			icon: <LevelsIcon />,
			heading: t("Adjust image"),
			summary: t(
				"Using controls below, add/reduce detail, brightness, contrast or remove the background completely.",
			),
			menu: <AdjustmentsMenu mode={mode} hasCode={!!code} />,
		},
		(() => {
			// if (!code) {
			// 	return {
			// 		id: "complete" as const,
			// 		buttonId: "editor-complete-button",
			// 		label: t("Finish"),
			// 		icon: <CheckIcon size={24} />,
			// 		heading: t("Buy a kit"),
			// 		summary: t(
			// 			"If you’re happy with your results, purchase a kit today and start building!",
			// 		),
			// 		menu: <TryItOutCompleteMenu onBackToHome={onRestart} />,
			// 	};
			// }
			if (!code) {
				return {
					id: "complete" as const,
					buttonId: "editor-complete-button",
					label: t("Finish"),
					icon: <CheckIcon size={24} />,
					heading: t("Buy a kit"),
					summary: t("Buy a kit functionality is coming soon!"),
					menu: <TryItOutCompleteMenu onBackToHome={onRestart} />,
				};
			}

			if (mode === "kit-only") {
				return {
					id: "complete" as const,
					buttonId: "editor-kit-complete-button",
					label: t("Finish"),
					icon: <CheckIcon size={24} />,
					heading: t("Complete"),
					summary: t(
						"All done! Click the button below to get your instructions.",
					),
					menu: (
						<CompleteKitOnlyMenu
							code={code}
							numberOfKits={numberOfKits}
							onChangeMode={onChangeMode}
						/>
					),
				};
			}

			return {
				id: "complete" as const,
				buttonId: "editor-non-kit-complete-button",
				label: t("Finish"),
				icon: <CheckIcon size={24} />,
				heading: t("Complete + Color"),
				summary: t(
					"If you’re happy with your results, you can download instructions below and buy the colour brick packs.",
				),
				menu: (
					<CompleteColoursMenu
						code={code}
						numberOfKits={numberOfKits}
						onChangeMode={onChangeMode}
						onOpenSummary={() => setSummaryOpen("cart")}
					/>
				),
			};
		})(),
	];
	const [activeStepIndex, setActiveStepIndex] = useState(0);
	const activeStep = steps[activeStepIndex];

	// Colour walk through
	const [colourWalkthroughSeen, setColourWalkthroughSeen] = useState(false);

	return (
		<>
			<Joyride
				run={mode === "colour" && !colourWalkthroughSeen}
				continuous
				spotlightPadding={5}
				tooltipComponent={EditorTooltip}
				callback={(c) => {
					if (c.action === "reset") {
						setColourWalkthroughSeen(true);
					}
				}}
				steps={[
					{
						disableBeacon: true,
						target: `#${adjustmentsButtonId}`,
						data: { title: t("Adjustments") },
						content: t(
							"We’re back in the Adjustments - Step 2, here you’ll be able to now add Colours and change Saturation.",
						),
					},
					{
						disableBeacon: true,
						target: `#${coloursButtonId}`,
						data: { title: t("Add Colors") },
						content: t(
							"You can use this tool now to adjust how many colours are used, which will determine how many additional colour bricks are needed.",
						),
					},
					{
						disableBeacon: true,
						target: `#${saturationButtonId}`,
						data: { title: t("Change Saturation") },
						content: t(
							"Using this tool you can determine how vivid the colours are in your image.",
						),
					},
					{
						disableBeacon: true,
						target: `#${coloursCostSummaryId}`,
						data: { title: t("Brick Summary") },
						content: t(
							"Here you can see a summary of the additional bricks that will be needed and estimated costs. Click on the price to change currency.",
						),
					},
				]}
				styles={{
					options: {
						arrowColor: "white",
						overlayColor: "rgba(8, 40, 76, 0.5)",
						zIndex: 100_000,
					},
				}}
			/>
			<ConfirmRestartModal
				id={confirmRestartModalId}
				onBackToUpload={onBackToUpload}
				onBackToHomePage={onRestart}
				onReset={resetPicture}
			/>
			<div className={classes["editor"]}>
				<header>
					<div className={classes["header-content"]}>
						{activeStepIndex === 0 ? (
							<Button
								type="button"
								variant="secondary"
								commandfor={confirmRestartModalId}
								command="show-modal"
								className={classes["nav-button"]}
							>
								<ArrowLeftIcon size={12} />
								{t("Back")}
							</Button>
						) : (
							<Button
								type="button"
								variant="secondary"
								onClick={() => {
									setActiveStepIndex((p) => Math.max(0, p - 1));
								}}
								className={classes["nav-button"]}
							>
								<ArrowLeftIcon size={12} />
								{t("Back")}
							</Button>
						)}
						<nav className={classes["steps"]}>
							{steps.map((s, i) => (
								<button
									key={s.label}
									id={s.buttonId}
									type="button"
									onClick={() => setActiveStepIndex(i)}
									aria-label={s.label}
									className={clsx(
										classes["step"],
										i === activeStepIndex && classes["step-active"],
									)}
								>
									<div className={classes["icon"]}>{s.icon}</div>
									<div className={classes["step-label"]}>{s.label}</div>
								</button>
							))}
						</nav>
						<Button
							type="button"
							variant="secondary"
							onClick={() =>
								setActiveStepIndex((p) => Math.min(steps.length - 1, p + 1))
							}
							className={clsx(
								activeStepIndex >= steps.length - 1 &&
									classes["next-button-hidden"],
								classes["nav-button"],
							)}
						>
							{t("Next")}
							<ArrowRightIcon size={12} />
						</Button>
					</div>
				</header>
				<main className={classes["main"]}>
					{!!code && mode === "colour" && (
						<ColoursCostSummary
							headerContentId={coloursCostSummaryId}
							open={summaryOpen}
							onChangeOpen={setSummaryOpen}
						/>
					)}
					<section
						className={clsx(
							classes["step-details"],
							showStepDetails && classes["step-details-show"],
						)}
					>
						<div className={classes["current-step-indicator"]}>
							{t("STEP")} {activeStepIndex + 1}
						</div>
						<div className={classes["current-step-content"]}>
							<h1 className="heading-1">{activeStep.heading}</h1>
							<SummaryText className={classes["step-summary"]}>
								{activeStep.summary}
							</SummaryText>
						</div>
						<button
							type="button"
							className={classes["step-details-toggle"]}
							onClick={() => {
								setShowStepDetails(!showStepDetails);
							}}
						>
							<ChevronUpIcon />
						</button>
					</section>
					<div className={classes["picture-render-container"]}>
						<PictureRender
							className={classes["picture-render"]}
							overlay={showLayoutOverlay ? layoutOverlay : undefined}
						/>
					</div>
				</main>
				{activeStep.menu}
			</div>
		</>
	);
}

type EditorContainerProps = EditorProps & {
	readonly sourceImage: SourceImage;
	readonly systemPalette: Palette;
};

function EditorContainer({
	sourceImage,
	numberOfKits,
	systemPalette,
	...rest
}: EditorContainerProps) {
	return (
		<EditorProvider
			sourceImage={sourceImage}
			systemPalette={systemPalette}
			numberOfKits={numberOfKits}
		>
			<Editor numberOfKits={numberOfKits} {...rest} />
		</EditorProvider>
	);
}

export default EditorContainer;
